import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Rating from '@mui/material/Rating';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import { Units, rateFood } from '../services/foods';
import { MenuFoodDto, regenerateFoodSuggestion, excludeFoodPermanently } from '../services/menus';
import { getAllFoodGroups, FoodGroupDto } from '../services/foodGroups';

interface FoodDetailModalProps {
  menuFood: MenuFoodDto | null;
  open: boolean;
  onClose: () => void;
  menuId?: string;
  onRegenerate?: () => void;
  showRegenerateButton?: boolean;
}

function formatUnits(units: Units): string {
  switch (units) {
    case Units.Piece: return 'ks';
    case Units.Liter: return 'l';
    case Units.Pinch: return 'špetka';
    case Units.Deciliter: return 'dl';
    case Units.Clove: return 'stroužek';
    case Units.Kilogram: return 'kg';
    case Units.Gram: return 'g';
    case Units.TableSpoon: return 'lžíce';
    case Units.TeaSpoon: return 'lžička';
    case Units.Milliliter: return 'ml';
    case Units.Handful: return 'hrst';
    case Units.Sprig: return 'snítka';
    case Units.Crucible: return 'kelímek';
    case Units.Can: return 'plechovka';
    case Units.Slice: return 'plátek';
    case Units.Volume: return 'objem';
    case Units.Stem: return 'stonek';
    case Units.Cup: return 'šálek';
    case Units.Mug: return 'hrnek';
    case Units.GlassOf: return 'sklenice';
    case Units.Cube: return 'kostka';
    case Units.Package: return 'balení';
    case Units.Bowl: return 'miska';
    case Units.Bottle: return 'láhev';
    default: return '';
  }
}

export default function FoodDetailModal({ 
  menuFood, 
  open, 
  onClose, 
  menuId, 
  onRegenerate,
  showRegenerateButton = false 
}: FoodDetailModalProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [regenerating, setRegenerating] = useState(false);
  const [excluding, setExcluding] = useState(false);
  const [ratingValue, setRatingValue] = useState<number | null>(null);
  const [rating, setRating] = useState(false);
  const [foodGroups, setFoodGroups] = useState<FoodGroupDto[]>([]);
  const [regenerateGroupId, setRegenerateGroupId] = useState('');

  // Reset the interactive rating when a different food is opened.
  useEffect(() => {
    setRatingValue(null);
    setRegenerateGroupId('');
  }, [menuFood?.id]);

  // Food groups are only needed for the regenerate picker, so only fetch when it's shown.
  useEffect(() => {
    if (!open || !showRegenerateButton) return;
    getAllFoodGroups()
      .then(groups => {
        const normalized = Array.isArray(groups)
          ? groups
          : Array.isArray((groups as unknown as { $values?: FoodGroupDto[] })?.$values)
            ? (groups as unknown as { $values?: FoodGroupDto[] }).$values!
            : [];
        setFoodGroups(normalized);
      })
      .catch(err => console.error('Error fetching food groups for regenerate picker:', err));
  }, [open, showRegenerateButton]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleRegenerate = async () => {
    if (!menuId || !menuFood) return;
    
    try {
      setRegenerating(true);
      await regenerateFoodSuggestion(menuId, menuFood.id, regenerateGroupId ? [regenerateGroupId] : undefined);
      setSnackbar({
        open: true,
        message: 'Nový návrh se připravuje...',
        severity: 'success'
      });
      onClose();
      onRegenerate?.();
    } catch (err) {
      console.error('Error regenerating food suggestion:', err);
      setSnackbar({
        open: true,
        message: 'Nepodařilo se vygenerovat nový návrh. Zkuste to znovu.',
        severity: 'error'
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleExclude = async () => {
    if (!menuId || !menuFood) return;

    try {
      setExcluding(true);
      await excludeFoodPermanently(menuId, menuFood.id);
      setSnackbar({
        open: true,
        message: 'Jídlo už nebude nabízeno. Připravuje se náhrada...',
        severity: 'success'
      });
      onClose();
      onRegenerate?.();
    } catch (err) {
      console.error('Error excluding food:', err);
      setSnackbar({
        open: true,
        message: 'Nepodařilo se jídlo vyřadit. Zkuste to znovu.',
        severity: 'error'
      });
    } finally {
      setExcluding(false);
    }
  };

  const handleRate = async (value: number | null) => {
    if (!menuFood || value == null) return;

    setRatingValue(value);
    try {
      setRating(true);
      await rateFood(menuFood.food.id, value);
      setSnackbar({
        open: true,
        message: 'Děkujeme za hodnocení!',
        severity: 'success'
      });
      onRegenerate?.();
    } catch (err) {
      console.error('Error rating food:', err);
      setSnackbar({
        open: true,
        message: 'Hodnocení se nepodařilo uložit. Zkuste to znovu.',
        severity: 'error'
      });
    } finally {
      setRating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (!menuFood) return null;

  const food = menuFood.food;
  const discountedProducts = menuFood.discountedProducts || [];

  // Group discounted products by supplier name (now provided by backend)
  const discountsBySupplier = discountedProducts.reduce((acc, dp) => {
    const supplierName = dp.supplierName || dp.supplierId;
    if (!acc[supplierName]) {
      acc[supplierName] = [];
    }
    acc[supplierName].push(dp);
    return acc;
  }, {} as Record<string, typeof discountedProducts>);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth={false}
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '800px' },
          maxWidth: '800px',
          m: { xs: 0, sm: 2 },
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span" noWrap sx={{ flex: 1 }}>
          {food.name}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Header: Image + Basic Info side by side on desktop */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 3,
          }}
        >
          {/* Image */}
          {food.images && food.images.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: '100%', sm: 200 },
                height: { xs: 180, sm: 200 },
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src={food.images[0].path}
                alt={food.name}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          )}

          {/* Basic Info */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {food.durationMinutes} minut
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PeopleIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {food.peopleCount} {food.peopleCount === 1 ? 'osoba' : food.peopleCount < 5 ? 'osoby' : 'osob'}
                </Typography>
              </Box>
              {food.ratingCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={food.averageRating} precision={0.5} size="small" readOnly />
                  <Typography variant="body2" color="text.secondary">
                    {food.averageRating.toFixed(1)} ({food.ratingCount})
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Vaše hodnocení
              </Typography>
              <Rating
                value={ratingValue}
                max={5}
                disabled={rating}
                onChange={(_, value) => handleRate(value)}
              />
            </Box>

            {food.categoryNames && food.categoryNames.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Kategorie
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {food.categoryNames.map((category, index) => (
                    <Chip key={index} label={category} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Discounts in basic info section */}
            {Object.keys(discountsBySupplier).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalOfferIcon fontSize="small" color="success" />
                  Slevy
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {Object.entries(discountsBySupplier).map(([supplierName, products]) => (
                    <Chip 
                      key={supplierName}
                      label={`${supplierName} (${products.length})`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Content: Ingredients + Recipe side by side on desktop */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {/* Ingredients - compact list */}
          {food.ingredients && food.ingredients.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                width: { xs: '100%', sm: 220 },
                flexShrink: 0,
                p: 1.5,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Ingredience
              </Typography>
              <List dense disablePadding>
                {food.ingredients.map((ingredient, index) => (
                  <Box key={ingredient.id}>
                    <ListItem disablePadding sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={ingredient.ingredientName}
                        secondary={`${ingredient.quantity} ${formatUnits(ingredient.units)}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    {index < food.ingredients.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Paper>
          )}

          {/* Recipe - takes remaining space */}
          {food.recipe && (
            <Paper variant="outlined" sx={{ flex: 1, p: 1.5, minWidth: 0 }}>
              <Typography variant="subtitle2" gutterBottom>
                Recept
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {food.recipe}
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Detailed discounts section (if any) */}
        {Object.keys(discountsBySupplier).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferIcon fontSize="small" color="success" />
              Detail slev
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(discountsBySupplier).map(([supplierName, products]) => (
                <Paper key={supplierName} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" fontWeight="medium" color="success.main">
                    {supplierName}
                  </Typography>
                  {products.some(p => (p.supplierBranchNames || []).length > 0) && (
                    <Typography variant="caption" color="text.secondary">
                      Pobočky: {Array.from(new Set(products.flatMap(p => p.supplierBranchNames || []))).join(', ') || 'Všechny'}
                    </Typography>
                  )}
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {products.map((product, idx) => (
                      <Chip 
                        key={idx}
                        label={
                          product.price != null 
                            ? `${product.productName || product.productId} - ${product.price.toFixed(2)} Kč`
                            : product.productName || product.productId
                        }
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {showRegenerateButton && menuId && foodGroups.length > 0 && (
          <Box sx={{ width: '100%' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="regenerate-group-label">Návrh podle skupiny</InputLabel>
              <Select
                labelId="regenerate-group-label"
                label="Návrh podle skupiny"
                value={regenerateGroupId}
                onChange={(e) => setRegenerateGroupId(e.target.value)}
                disabled={regenerating || excluding}
              >
                <MenuItem value="">Podle mého nastavení</MenuItem>
                {foodGroups.map(g => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Platí jen pro tento jeden návrh, vaše obecné nastavení skupin se nezmění. Vaše alergeny, doba
              vaření a hodnocení se stále uplatní. Pokud skupina obsahuje jen vegetariánská (nebo jen masitá)
              jídla, oba návrhy dne budou tohoto typu.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: showRegenerateButton ? 'space-between' : 'flex-end', width: '100%' }}>
          {showRegenerateButton && menuId && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                onClick={handleRegenerate}
                color="warning"
                variant="outlined"
                disabled={regenerating || excluding}
                startIcon={regenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {regenerating ? 'Generuji...' : 'Vygenerovat nový návrh'}
              </Button>
              <Button
                onClick={handleExclude}
                color="error"
                variant="outlined"
                disabled={regenerating || excluding}
                startIcon={excluding ? <CircularProgress size={16} /> : <BlockIcon />}
              >
                {excluding ? 'Vyřazuji...' : 'Nenabízet nikdy znovu'}
              </Button>
            </Box>
          )}
          <Button onClick={onClose}>Zavřít</Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
